export type PluralCategory = 'zero' | 'one' | 'two' | 'few' | 'many' | 'other';

export type PluralForms = Readonly<
  { one: string; other: string }
  & Partial<Record<Exclude<PluralCategory, 'one' | 'other'>, string>>
>;

export interface PluralOptions {
  locale?: string | string[];
}

/** Formats a number through the same locale policy used by count phrases. */
export function formatNumber(
  value: number,
  options: PluralOptions = {},
): string {
  return new Intl.NumberFormat(options.locale).format(value);
}

/** Selects a typed word form using the browser locale's cardinal rules. */
export function plural(
  count: number,
  forms: PluralForms,
  options: PluralOptions = {},
): string {
  const category = new Intl.PluralRules(options.locale).select(count) as PluralCategory;
  return forms[category] ?? forms.other;
}

/** Formats a locale-aware count and its selected word form as one phrase. */
export function formatCount(
  count: number,
  forms: PluralForms,
  options: PluralOptions = {},
): string {
  const formattedCount = formatNumber(count, options);
  return `${formattedCount} ${plural(count, forms, options)}`;
}

/** Formats an optional numeric count or returns a non-identifying fallback phrase. */
export function formatCountOrFallback(
  value: unknown,
  forms: PluralForms,
  fallback: string,
  options: PluralOptions = {},
): string {
  const count = typeof value === 'number'
    ? value
    : typeof value === 'string' && value.trim() !== ''
      ? Number(value)
      : Number.NaN;
  return Number.isFinite(count)
    ? formatCount(count, forms, options)
    : `${fallback} ${forms.other}`;
}
